import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Dropdown, Space, Badge } from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  StarOutlined,
  BookOutlined,
  ProfileOutlined,
  ShoppingCartOutlined,
  HeartOutlined,
  MessageOutlined,
  DashboardOutlined,
  MenuOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import '../auth.css';
import Links from './NavigationLinks/Links';
import { NotificationDropdown } from '../components/Notifications/NotificationDropdown';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useLogout } from '../hooks/useLogout';
import { MobileMenu } from '../components/Navigation/MobileMenu/MobileMenu';

export default function MainHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { logout } = useLogout();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'overview',
      icon: <DashboardOutlined />,
      label: t('nav.overview'),
      onClick: () => navigate('/dashboard/overview'),
    },
    {
      key: 'create-listing',
      icon: <PlusOutlined style={{ color: '#fe5a1d' }} />,
      label: <span style={{ color: '#fe5a1d', fontWeight: 600 }}>{t('nav.create_listing')}</span>,
      onClick: () => navigate('/dashboard/add-user-listing'),
    },
    { type: 'divider' },
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: t('nav.my_profile'),
      onClick: () => navigate('/dashboard/profile'),
    },
    {
      key: 'my-ads',
      icon: <ShoppingCartOutlined />,
      label: t('nav.my_advertisements'),
      onClick: () => navigate('/dashboard/ads'),
    },
    {
      key: 'favorites',
      icon: <HeartOutlined />,
      label: t('nav.my_favorites'),
      onClick: () => navigate('/dashboard/favorites'),
    },
    {
      key: 'messages',
      icon: <MessageOutlined />,
      label: t('nav.messages'),
      onClick: () => navigate('/dashboard/messages'),
    },
    { type: 'divider' },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('nav.settings'),
      onClick: () => navigate('/dashboard/settings'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: logout,
      danger: true,
    },
  ];

  const renderProfileDropdown = () => {
    const handlePlusClick = () => {
      navigate(isAuthenticated ? '/dashboard/add-user-listing' : '/login');
    };

    if (!isAuthenticated) {
      return (
        <Space size="middle" className="align-items-center">
          <PlusCircleOutlined
            style={{ fontSize: '20px', color: 'var(--color1)', cursor: 'pointer', marginRight: '10px' }}
            onClick={handlePlusClick}
            title={t('nav.create_listing', 'Create Listing')}
          />
          <Link to="/login" className="btn-nav-login text-decoration-none">
            {t('nav.login')}
          </Link>
          <Link to="/register" className="btn-nav-register text-decoration-none">
            {t('nav.register')}
          </Link>
        </Space>
      );
    }

    if (user?.role === 'ADMIN') {
      return (
        <button
          onClick={() => navigate('/admin')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
            background: '#fe5a1d',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <DashboardOutlined />
          {t('nav.admin_panel')}
        </button>
      );
    }

    return (
      <Space size="middle">
        {/* Notifications */}
        <div className="d-lg-flex gap-3 align-items-center d-none">
          <NotificationDropdown />
          <PlusCircleOutlined
            style={{ fontSize: '20px', color: 'var(--color1)', cursor: 'pointer' }}
            onClick={handlePlusClick}
            title={t('nav.create_listing', 'Create Listing')}
          />
        </div>

        {/* Profile Dropdown */}
        <Dropdown
          menu={{ items: profileMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
              minWidth: '150px',
            }}
            onMouseEnter={e =>
              (e.currentTarget.style.backgroundColor = '#f5f5f5')
            }
            onMouseLeave={e =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
          >
            <Avatar
              size={32}
              icon={<UserOutlined />}
              style={{ marginRight: '8px' }}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  lineHeight: '1.2',
                }}
              >
                {user ? `${user.firstName} ${user.lastName}` : t('nav.user_default')}
              </span>
              <span
                style={{ fontSize: '12px', color: '#666', lineHeight: '1.2' }}
              >
                {user?.role || t('nav.user_default')}
              </span>
            </div>
          </div>
        </Dropdown>
      </Space>
    );
  };

  return (
    <>
      <nav className={`navbar navbar-expand-lg navbar-light main-header py-3 home-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          {/* Logo */}
          <a className="navbar-brand me-0" href="/">
            <img alt="Logo" height="30" src="/images/logos/AutoPlace.png" />
          </a>

          {/* Hamburger (mobile) */}
          <button
            className="navbar-toggler border-0"
            type="button"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <MenuOutlined
              style={{ fontSize: '24px', color: 'var(--color1)' }}
            />
          </button>

          {/* Right side (desktop) */}
          <div className="collapse navbar-collapse d-none d-lg-flex justify-content-end">
            <Links />
            <div className="d-flex gap-3 align-items-center btn_section">
              {renderProfileDropdown()}
            </div>
          </div>
        </div>
      </nav>

      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)} 
      />
    </>
  );
}
